import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';
import { PrivacyPolicy, TermsOfService, LEGAL_VERSION, LAST_UPDATED } from './content';
import './legal.css';

const DOCS = {
  terms:   { label: 'Terms of Service', path: '/terms',   Body: TermsOfService },
  privacy: { label: 'Privacy Policy',   path: '/privacy', Body: PrivacyPolicy },
};

// Shown instead of the app until the signed-in user has accepted the current
// Terms of Service and Privacy Policy — at first use, and again whenever
// either document changes (LEGAL_VERSION). Acceptance is recorded on the
// server with the version and time.
//
// A document counts as viewed once its tab has been opened (the Terms open
// first) or its end has come into view. The tick box unlocks when both have
// been viewed. (Earlier versions required a scroll-to-the-end measurement,
// which failed on zoomed pages and touch scrolling and left users stuck.)
export default function LegalGate() {
  const { user, legal, setLegal, logout } = useAuth();
  const [tab, setTab]       = useState('terms');
  const [seen, setSeen]     = useState({ terms: true, privacy: false });
  const [agreed, setAgreed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const readerRef = useRef(null);
  const endRef    = useRef(null);

  const updated = !!legal?.accepted_version; // accepted an older version before

  const markSeen = (t) => setSeen(s => (s[t] ? s : { ...s, [t]: true }));

  const openTab = (t) => {
    setTab(t);
    markSeen(t);
    if (readerRef.current) readerRef.current.scrollTop = 0;
  };

  // Reaching the end of the open document also counts as having read it.
  useEffect(() => {
    const root = readerRef.current, end = endRef.current;
    if (!root || !end || typeof IntersectionObserver === 'undefined') return undefined;
    const obs = new IntersectionObserver(
      (entries) => { if (entries.some(e => e.isIntersecting)) markSeen(tab); },
      { root, threshold: 0 }
    );
    obs.observe(end);
    return () => obs.disconnect();
  }, [tab]);

  const bothSeen = seen.terms && seen.privacy;
  const next = !seen.privacy ? 'privacy' : !seen.terms ? 'terms' : null;
  const { Body } = DOCS[tab];

  const accept = async () => {
    setSaving(true); setError('');
    try {
      const { data } = await authAPI.acceptTerms({ accept: true, version: LEGAL_VERSION });
      setLegal(data.legal);
    } catch (err) {
      setError(err.response?.status === 400
        ? 'These documents were just updated — please reload the app to read the latest version.'
        : 'Could not save — check your connection and try again.');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div className="card" style={{ width: '100%', maxWidth: 640 }}>
        <h1 className="page-title" style={{ marginBottom: '0.35rem' }}>
          {updated ? 'We have updated our terms' : 'Before you start'}
        </h1>
        <p className="text-muted" style={{ marginBottom: '0.9rem' }}>
          Hi {user?.name?.split(' ')[0] || 'there'} — please read ShopBook's Terms of Service and Privacy Policy
          (updated {LAST_UPDATED}). They explain how the service works, what we do with your information, and
          your rights.
        </p>

        <div className="legal-tabs" role="tablist">
          {Object.entries(DOCS).map(([key, d]) => (
            <button key={key} role="tab" aria-selected={tab === key}
              className={tab === key ? 'btn-primary' : 'btn-ghost'} onClick={() => openTab(key)}>
              {d.label} {seen[key] ? '✓' : ''}
            </button>
          ))}
        </div>

        <div className="legal-reader" ref={readerRef} tabIndex={0} aria-label={DOCS[tab].label}>
          <Body />
          <div ref={endRef} style={{ height: 1 }} aria-hidden="true" />
        </div>
        <p style={{ fontSize: '0.78rem', margin: '-0.35rem 0 0.75rem' }}>
          <a href={DOCS[tab].path} target="_blank" rel="noreferrer">Open the {DOCS[tab].label} in a full page ↗</a>
        </p>

        {next && (
          <p style={{ fontSize: '0.82rem', marginBottom: '0.6rem', color: 'var(--color-warning)' }}>
            Now open the{' '}
            <button type="button" onClick={() => openTab(next)}
              style={{ background: 'none', border: 'none', padding: 0, color: 'var(--color-accent)',
                textDecoration: 'underline', cursor: 'pointer', font: 'inherit' }}>
              {DOCS[next].label}
            </button>{' '}
            tab to continue.
          </p>
        )}

        <label className="legal-check" style={{ opacity: bothSeen ? 1 : 0.5, cursor: bothSeen ? 'pointer' : 'not-allowed' }}>
          <input type="checkbox" checked={agreed} disabled={!bothSeen} onChange={e => setAgreed(e.target.checked)} />
          <span>I have read and agree to the Terms of Service and the Privacy Policy.</span>
        </label>

        {error && <p className="error-msg">{error}</p>}

        <button className="btn-primary" style={{ width: '100%', padding: '0.8rem', marginTop: '0.9rem' }}
          disabled={!bothSeen || !agreed || saving} onClick={accept}>
          {saving ? 'Saving…' : 'Agree and continue'}
        </button>
        <button className="btn-ghost" style={{ width: '100%', marginTop: '0.6rem' }} onClick={() => logout({ force: true })}>
          Log out
        </button>
      </div>
    </div>
  );
}
